import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { GlobalSearch } from "@/components/GlobalSearch";
import { ROLE_LABELS } from "@/lib/format";
import {
  LayoutDashboard, FileText, CheckSquare, Users as UsersIcon,
  LogOut, Menu, X, Landmark, Banknote, UserCog, ScrollText, ReceiptText, Truck, Building2, Receipt, FileBarChart,
  ShoppingCart, HandCoins, PiggyBank, Contact, ListOrdered, History, ImageUp, ChevronDown,
} from "lucide-react";
import { PageTransition } from "@/components/PageTransition";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["it_admin", "direktur", "approver", "tim_keuangan", "pemohon", "staf_tagihan", "tim_kendaraan"], end: true },
  { to: "/requests", label: "Pengajuan Kas", icon: FileText, roles: ["direktur", "approver", "tim_keuangan", "pemohon", "tim_kendaraan"] },
  { to: "/expenses", label: "Pengeluaran", icon: Receipt, roles: ["pemohon", "tim_kendaraan"] },
  { to: "/transactions", label: "Input Transaksi", icon: ShoppingCart, roles: ["pemohon"], sales: true },
  { to: "/transaction-list", label: "Daftar Transaksi", icon: ListOrdered, roles: ["pemohon"], sales: true },
  { to: "/customers", label: "Data Customer", icon: Contact, roles: ["pemohon"], sales: true },
  { to: "/receivables", label: "Piutang", icon: HandCoins, roles: ["pemohon"], sales: true },
  { to: "/cash-deposits", label: "Setor Kas", icon: PiggyBank, roles: ["pemohon", "direktur", "tim_keuangan", "it_admin"], sales: true },
  { to: "/transfer-proofs", label: "Bukti Transfer", icon: ImageUp, roles: ["direktur", "tim_keuangan", "it_admin"] },
  { to: "/admin/transactions", label: "Semua Transaksi", icon: ShoppingCart, roles: ["it_admin"] },
  { to: "/all-expenses", label: "Semua Pengeluaran", icon: Receipt, roles: ["it_admin"] },
  { to: "/approvals", label: "Antrian Approval", icon: CheckSquare, roles: ["direktur", "approver"] },
  { to: "/disbursement", label: "Pencairan Kas", icon: Banknote, roles: ["tim_keuangan", "direktur", "it_admin"] },
  { to: "/vendor-bills", label: "Tagihan Vendor", icon: ReceiptText, roles: ["staf_tagihan", "direktur", "tim_keuangan", "it_admin"] },
  { to: "/vehicles", label: "Data Kendaraan", icon: Truck, roles: ["tim_kendaraan", "direktur", "it_admin"] },
  { to: "/vehicle-bills", label: "Tagihan Kendaraan", icon: Receipt, roles: ["tim_kendaraan", "direktur", "tim_keuangan", "it_admin"] },
  { to: "/vehicle-cash-history", label: "Potongan Kas Kendaraan", icon: History, roles: ["direktur", "tim_keuangan", "it_admin"] },
  { to: "/laporan", label: "Laporan Keuangan", icon: FileBarChart, roles: ["direktur", "tim_keuangan", "it_admin", "approver", "tim_kendaraan"] },
  { to: "/delegasi", label: "Delegasi Approver", icon: UserCog, roles: ["direktur"] },
  { to: "/branches", label: "Master Cabang", icon: Building2, roles: ["it_admin"] },
  { to: "/companies", label: "Master PT", icon: Landmark, roles: ["it_admin", "direktur", "staf_tagihan", "tim_keuangan"] },
  { to: "/users", label: "Manajemen User", icon: UsersIcon, roles: ["it_admin"] },
  { to: "/audit", label: "Log Audit", icon: ScrollText, roles: ["it_admin"] },
];

const GROUPED_ROLES = ["direktur", "tim_keuangan", "it_admin"];
const GROUPS = [
  { title: "", paths: ["/"] },
  { title: "Kas", paths: ["/requests", "/approvals", "/disbursement", "/cash-deposits", "/transfer-proofs"] },
  { title: "Tagihan", paths: ["/vendor-bills", "/vehicle-bills", "/vehicle-cash-history"] },
  { title: "Kendaraan", paths: ["/vehicles"] },
  { title: "Laporan", paths: ["/laporan", "/admin/transactions", "/all-expenses"] },
];
const SETTINGS = { title: "Pengaturan", paths: ["/delegasi", "/companies", "/branches", "/users", "/audit"] };

function usePendingApprovals(enabled) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => api.get("/cash-requests", { params: { status: "pending" } }).then((r) => setN(r.data.length)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [enabled]);
  return n;
}

function NavItem({ n, badge, onClick }) {
  return (
    <NavLink to={n.to} end={n.end} onClick={onClick}
      data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-white text-brand shadow-sm" : "text-emerald-50/90 hover:bg-white/10"}`}>
      <n.icon className="h-4 w-4" />
      <span className="flex-1">{n.label}</span>
      {badge > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="nav-approval-badge">{badge > 99 ? "99+" : badge}</span>}
    </NavLink>
  );
}

const NAV_OPEN_KEY = "etf_nav_open";
const inGroup = (g, path) => g.paths.some((p) => (p === "/" ? path === "/" : path === p || path.startsWith(`${p}/`)));

function useOpenGroups(path) {
  const [openSet, setOpenSet] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(NAV_OPEN_KEY) || "[]"); } catch { return []; }
  });
  useEffect(() => { sessionStorage.setItem(NAV_OPEN_KEY, JSON.stringify(openSet)); }, [openSet]);
  useEffect(() => {
    const g = [...GROUPS, SETTINGS].find((x) => x.title && inGroup(x, path));
    if (g) setOpenSet((s) => (s.includes(g.title) ? s : [...s, g.title]));
  }, [path]);
  const toggle = (t) => setOpenSet((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));
  return [openSet, toggle];
}

function NavGroup({ g, items, badge, onClick, isOpen, onToggle }) {
  const list = g.paths.map((p) => items.find((n) => n.to === p)).filter(Boolean);
  if (!list.length) return null;
  const slug = (g.title || "main").toLowerCase();
  const groupBadge = list.some((n) => n.to === "/approvals") ? badge : 0;
  const show = !g.title || isOpen;
  return (
    <div className="space-y-1" data-testid={`nav-group-${slug}`}>
      {g.title && (
        <button type="button" onClick={() => onToggle(g.title)} aria-expanded={isOpen} data-testid={`nav-group-toggle-${slug}`}
          className="w-full flex items-center gap-2 px-3 pt-3 pb-1 text-[10px] font-mono uppercase tracking-widest text-emerald-200/70 hover:text-white transition-colors">
          <span className="flex-1 text-left">{g.title}</span>
          {!isOpen && groupBadge > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center tracking-normal" data-testid={`nav-group-badge-${slug}`}>{groupBadge > 99 ? "99+" : groupBadge}</span>}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? "rotate-0" : "-rotate-90"}`} />
        </button>
      )}
      {show && list.map((n) => <NavItem key={n.to} n={n} badge={n.to === "/approvals" ? badge : 0} onClick={onClick} />)}
    </div>
  );
}

function HeaderClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const tgl = now.toLocaleDateString("id-ID", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  const jam = now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  return (
    <div className="hidden sm:block text-right leading-tight" data-testid="header-clock">
      <p className="text-[11px] text-emerald-200/80 font-mono">{tgl}</p>
      <p className="text-sm font-semibold font-mono" data-testid="header-time">{jam}</p>
    </div>
  );
}

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes(user?.role) && !(n.sales && user?.role === "pemohon" && !user?.sales_enabled));
  const grouped = GROUPED_ROLES.includes(user?.role);
  const badge = usePendingApprovals(grouped && items.some((n) => n.to === "/approvals"));
  const close = () => setOpen(false);
  const location = useLocation();
  const [openSet, toggleGroup] = useOpenGroups(location.pathname);
  const gp = (g) => ({ isOpen: openSet.includes(g.title), onToggle: toggleGroup });

  const handleLogout = async () => { sessionStorage.removeItem(NAV_OPEN_KEY); await logout(); navigate("/login"); };

  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 brand-gradient text-white flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-5 py-5 flex items-center gap-3 border-b border-white/10">
          <div className="h-10 w-10 rounded-xl bg-emerald-400/20 border border-emerald-300/30 flex items-center justify-center">
            <Landmark className="h-5 w-5 text-emerald-300" />
          </div>
          <div className="leading-tight">
            <p className="font-bold text-sm tracking-wide" style={{ fontFamily: "Outfit" }}>EXTRA TRUSS</p>
            <p className="text-[11px] text-emerald-200/80 font-mono tracking-widest">FINANCE</p>
          </div>
        </div>
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
        <nav className="flex-1 px-3 py-4 space-y-1">
          {grouped
            ? GROUPS.map((g) => <NavGroup key={g.title || "main"} g={g} items={items} badge={badge} onClick={close} {...gp(g)} />)
            : items.map((n) => <NavItem key={n.to} n={n} badge={n.to === "/approvals" ? badge : 0} onClick={close} />)}
        </nav>
        {grouped && (
          <div className="px-3 pb-3 border-t border-white/10">
            <NavGroup g={SETTINGS} items={items} badge={0} onClick={close} {...gp(SETTINGS)} />
          </div>
        )}
        </div>
        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="h-9 w-9 rounded-full bg-emerald-400 text-brand font-bold flex items-center justify-center text-sm">{initials}</div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[11px] text-emerald-200/80">{ROLE_LABELS[user?.role]}</p>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-emerald-50/90 hover:bg-white/10 transition-colors">
            <LogOut className="h-4 w-4" /> Keluar
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 brand-gradient text-white flex items-center justify-between px-4 lg:px-8 sticky top-0 z-20 shadow-md">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <div>
              <p className="text-xs text-emerald-200/80 font-mono uppercase tracking-widest">Portal Kas & Pengeluaran</p>
              <h1 className="text-base font-semibold" style={{ fontFamily: "Outfit" }}>Selamat datang, {user?.name?.split(" ")[0]}</h1>
            </div>
          </div>
          <div className="hidden md:block flex-1 max-w-sm mx-4"><GlobalSearch /></div>
          <div className="flex items-center gap-2 lg:gap-3">
            <HeaderClock />
            <span className="hidden md:inline-flex items-center px-3 py-1 rounded-full bg-white/10 text-xs font-medium border border-white/15">
              {ROLE_LABELS[user?.role]}
            </span>
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8 max-w-[1400px] w-full mx-auto"><PageTransition><ErrorBoundary>{children}</ErrorBoundary></PageTransition></main>
      </div>
    </div>
  );
}
